import { makeObservable, action, override } from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdate } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import {
  SessionEventKind,
  SessionEventModelData,
  SessionEventUpsertedSyncUpdateValue,
  SessionMessageEventKind,
} from "./types";
import { SessionEventObservable } from "./SessionEventObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import Dexie from "dexie";
import { JsonObject, Maybe } from "@/domains/common/types";
import { CreateSessionEventOperation } from "@/store/sync/operations/sessions/CreateSessionEventOperation";
import { uuidModule } from "@/modules/uuid";
import { Uuid } from "@/domains/global/identifiers";
import { InputFormKind } from "@/components/active-session/input-forms";
import { CreateMessageSentSessionEventOperation } from "@/store/sync/operations/events/CreateMessageSentEventOperation";
import { SessionEventIndexes } from "@/store/sessions/SessionEventIndexes";
import { AppStore } from "@/store/AppStore";
import { searchSessionEventsForSession } from "@/store/sessions/search/search-session-events";
import { DateTime } from "luxon";

export class AppStoreSessionEventStore extends BaseSyncModelStore<
  SessionEventObservable,
  SessionEventModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SessionEvent, ...injectedDeps });
    makeObservable<
      this,
      | "countMyChatMessagesSentSince"
      | "getManySessionEventsBySessionId"
      | "createMessageSentSessionEvent"
      | "createSyncModel"
    >(this, {
      countMyChatMessagesSentSince: true,
      getManySessionEventsBySessionId: true,
      createMessageSentSessionEvent: true,
      createSyncModel: false,
      createSessionEvent: action,
      createInputRequestSessionEvent: action,
      createInputResponseSessionEvent: action,
      computeIndexes: override,
      processSyncUpdate: override,
      processLiveSyncUpdate: action,
    });
  }

  createSyncModel(data: SyncUpdateValue<SessionEventModelData>): SessionEventObservable {
    return new SessionEventObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  getManySessionEventsBySessionId = async (sessionId: string): Promise<SessionEventModelData[]> => {
    return await Dexie.waitFor(searchSessionEventsForSession({ store: this.store, sessionId }));
  };

  /**
   * Creates a session event by executing the create session event sync operation.
   */
  public async createSessionEvent({
    session_id,
    event_kind,
    event_value,
  }: {
    session_id: string;
    event_kind: SessionEventKind;
    event_value: { kind: string } & Record<string, unknown>;
  }): Promise<void> {
    const id = uuidModule.generate();
    return new CreateSessionEventOperation({
      store: this.store,
      payload: {
        id,
        session_id,
        event_kind,
        value_json: event_value,
        locally_created_at: new Date().toISOString(),
      },
    }).execute();
  }

  public async createMessageSentSessionEvent({
    session_id,
    message_value,
  }: {
    session_id: string;
    message_value: { kind: SessionEventKind | SessionMessageEventKind } & Record<string, unknown>;
  }): Promise<void> {
    const id = uuidModule.generate();
    return new CreateMessageSentSessionEventOperation({
      store: this.store,
      payload: {
        id,
        session_id,
        event_kind: "MESSAGE_SENT",
        message_value_json: message_value,
        locally_created_at: new Date().toISOString(),
      },
    }).execute();
  }

  public async createInputRequestSessionEvent({
    sessionId,
    metadata,
    form,
  }: {
    sessionId: string;
    metadata: JsonObject;
    form: InputFormKind;
  }): Promise<void> {
    await this.createSessionEvent({
      session_id: sessionId,
      event_kind: "INPUT_REQUESTED",
      event_value: {
        kind: "INPUT_REQUESTED",
        id: uuidModule.generate(),
        form,
        metadata,
      },
    });
  }

  public async createInputResponseSessionEvent({
    sessionId,
    config,
    requestId,
    metadata,
    inputKind,
  }: {
    sessionId: string;
    config: JsonObject;
    requestId: Uuid;
    metadata: Record<string, unknown>;
    inputKind: string;
  }): Promise<void> {
    // Only serialize metadata which might contain complex objects
    const serializableMetadata = JSON.parse(JSON.stringify(metadata));

    return this.createSessionEvent({
      session_id: sessionId,
      event_kind: "INPUT_PROVIDED",
      event_value: {
        kind: "INPUT_PROVIDED",
        session_id: sessionId,
        value: config,
        request_id: requestId,
        input_kind: inputKind,
        metadata: serializableMetadata,
      },
    });
  }

  public computeIndexes({
    store,
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SessionEventModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SessionEventModelData>[];
  }): Record<string, unknown> {
    return new SessionEventIndexes({ store, remoteData, optimisticUpdates }).indexes;
  }

  /** **FEATURE GATES SHOULD BE INSTANTANEOUS, USE THE COMPUTED VALUE!!!** */
  async countMyChatMessagesSentSince(createdSince: DateTime): Promise<number> {
    const createdSinceDate = createdSince.toISO();

    // Use the compound index for efficient querying
    const compoundIndex = "[locally_created_at+kind+model_id]";
    const userActionSessionEventsCreatedSince = (await this.localTable
      .where(compoundIndex)
      .between(
        [createdSinceDate, "USER_ACTION_V1", Dexie.minKey],
        [Dexie.maxKey, "USER_ACTION_V1", Dexie.maxKey]
      )
      .keys()) as unknown as [locally_created_at: string, kind: string, model_id: string][];

    const sessionIds = userActionSessionEventsCreatedSince.map(
      ([_locallyCreatedAt, _kind, modelId]) => modelId
    );
    const sessions = await this.store.sessions.localTable.bulkGet(sessionIds);
    const sessionKindMap = new Map(
      sessions
        .filter(session => session !== undefined)
        .map(session => [session.model_id, session.model_data.kind])
    );

    let count = 0;
    for (const modelId of sessionIds) {
      const sessionKind = sessionKindMap.get(modelId);
      if (sessionKind === "GENERIC") count++;
    }

    return count;
  }

  public async processSyncUpdate(update: SyncUpdate<SessionEventModelData>) {
    await this.store.sync.actionQueue.removeAllOptimisticUpdatesByModelId(update.value.model_id);
    await super.processSyncUpdate(update);
  }

  async processLiveSyncUpdate(
    _sync_operation_id: string,
    value: SessionEventUpsertedSyncUpdateValue
  ): Promise<void> {
    // Remove any existing optimistic updates for this session event
    await this.store.sync.actionQueue.removeAllOptimisticUpdatesByModelId(value.model_id);
    await this.processSyncUpdate({ kind: "UPSERTED", value });
  }
}
